import { defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { validatePassword } from '../../../../utils/password-utils';
import { maxLength, regex } from '../../../../utils/validation';
import type { FieldConfig } from '../../../../components/field';
import type { RegisterCustomerDTO } from '../../model/RegisterCustomerDTO';

const messages = defineMessages({
    required: { defaultMessage: '{field} is required', id: 'error.field.required' },
    invalidFormat: { defaultMessage: 'Enter a valid {field}', id: 'error.field.invalid' },
    maxLength: {
        defaultMessage: 'No more than {max} characters',
        id: 'account.registration.error.max-length',
    },
    noSpecialChars: {
        defaultMessage: 'No special characters',
        id: 'account.registration.error.no-special-characters',
    },
    firstName: { defaultMessage: 'First Name', id: 'account.registration.first_name' },
    lastName: { defaultMessage: 'Last Name', id: 'account.registration.last_name' },
    email: {
        defaultMessage: 'Email',
        id: 'account.registration.email',
    },
    password: { defaultMessage: 'Password', id: 'account.registration.password' },
    signUpNewsletter: {
        defaultMessage: 'Sign up for emails to receive a special offer (new subscribers only).',
        id: 'account.registration.sign-up-newsletter',
    },
});

export function useRegistrationFields(
    form: UseFormReturn<RegisterCustomerDTO>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    const fields: Record<string, FieldConfig> = {
        firstName: {
            id: 'account-registration-first-name-field',
            name: 'firstName',
            label: intl.formatMessage(messages.firstName),
            defaultValue: '',
            type: 'text',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'First name' }),
                maxLength: {
                    value: maxLength.firstName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.firstName,
                    }),
                },
                pattern: {
                    value: regex.firstName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
            },
            error: errors.firstName,
            control,
            variant: 'new-style',
        },
        lastName: {
            id: 'account-registration-last-name-field',
            name: 'lastName',
            label: intl.formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Last name' }),
                maxLength: {
                    value: maxLength.lastName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.lastName,
                    }),
                },
                pattern: {
                    value: regex.lastName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
            },
            error: errors.lastName,
            control,
            variant: 'new-style',
        },
        email: {
            id: 'account-registration-email-field',
            name: 'email',
            label: intl.formatMessage(messages.email),
            type: 'email',
            defaultValue: '',
            rules: {
                required: intl.formatMessage(messages.required, { field: 'Email' }),
                pattern: {
                    value: regex.email,
                    message: intl.formatMessage(messages.invalidFormat, { field: 'email' }),
                },
            },
            error: errors.email,
            control,
            variant: 'new-style',
        },
        password: {
            id: 'account-registration-password-field',
            name: 'password',
            label: intl.formatMessage(messages.password),
            defaultValue: '',
            type: 'password',
            rules: {
                required: intl.formatMessage({
                    defaultMessage: 'Password is required',
                    id: 'account.registration.error.required_password',
                }),
                validate: {
                    hasMinChars: (val: string) =>
                        validatePassword(val).hasMinChars ||
                        intl.formatMessage({
                            defaultMessage: 'Password must contain at least 6 characters.',
                            id: 'account.registration.error.minimum_characters',
                        }),
                    hasNumber: (val: string) =>
                        validatePassword(val).hasNumber ||
                        intl.formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'account.registration.error.contain_number',
                        }),
                    hasAnyLetter: (val: string) =>
                        validatePassword(val).hasAnyLetter ||
                        intl.formatMessage({
                            defaultMessage: 'Password must contain at least one letter.',
                            id: 'account.registration.error.contain_any_letter',
                        }),
                },
            },
            error: errors.password,
            control,
            variant: 'new-style',
        },
        signUpNewsletter: {
            id: 'account-registration-accepts-marketing-label',
            name: 'signUpNewsletter',
            label: intl.formatMessage(messages.signUpNewsletter),
            defaultValue: true,
            type: 'checkbox',
            error: errors.signUpNewsletter,
            control,
            variant: 'new-style',
        },
    };

    return fields;
}
